import {
  Alert,
  Box,
  Button,
  Card,
  Icon,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { format as formatFns } from "date-fns";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useEffect, useState } from "react";
import ContasService from "services/accounts";
import FinanceiroService from "services/financeiro";
import { ptBR_DataGrid } from "utils/datagrid";
import { dataCalendario } from "utils/dataCalendario";
import LancamentoDialog, { TIPOS_LANCAMENTO } from "./lancamentoDialog";

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v) || 0
  );

/**
 * Lançamentos bancários: saldo informado do banco (ponto de partida da
 * movimentação) e entradas/saídas manuais (tarifas, transferências...).
 */
export default function LancamentosBancarios() {
  const hoje = new Date();
  const [account, setAccount] = useState("");
  const [tipo, setTipo] = useState("");
  const [inicio, setInicio] = useState(
    formatFns(
      new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1),
      "yyyy-MM-dd"
    )
  );
  const [fim, setFim] = useState(formatFns(hoje, "yyyy-MM-dd"));
  const [contas, setContas] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [dialogo, setDialogo] = useState(null); // null | {} novo | lançamento
  const [remover, setRemover] = useState(null);

  const buscar = async () => {
    setLoading(true);
    setErro("");
    try {
      const lista = await FinanceiroService.lancamentos({
        account,
        tipo,
        dataInicio: inicio,
        dataFim: fim,
      });
      setRows(lista.map((l) => ({ ...l, id: l._id })));
    } catch (e) {
      setErro(e?.error || "Erro ao carregar os lançamentos.");
    }
    setLoading(false);
  };

  useEffect(() => {
    ContasService.list()
      .then(({ data }) => setContas(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    buscar();
  }, [account, tipo, inicio, fim]);

  const excluir = async () => {
    try {
      await FinanceiroService.excluirLancamento(remover._id);
    } catch (e) {
      setErro(e?.error || "Erro ao excluir.");
    }
    setRemover(null);
    buscar();
  };

  const filtro = { size: "small", InputLabelProps: { shrink: true } };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <LancamentoDialog
          open={Boolean(dialogo)}
          lancamento={dialogo?._id ? dialogo : null}
          padrao={{ account }}
          onClose={() => setDialogo(null)}
          onSalvo={() => {
            setDialogo(null);
            buscar();
          }}
        />
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <MDTypography variant="h6" color="white" mt={1}>
              Saldos e lançamentos bancários
            </MDTypography>
            <MDButton variant="contained" onClick={() => setDialogo({})}>
              Novo lançamento
            </MDButton>
          </MDBox>
          <MDBox p={3}>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <TextField
                label="Conta bancária"
                select
                SelectProps={{ native: true }}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                sx={{ minWidth: 200 }}
                {...filtro}
              >
                <option value="">Todas</option>
                {contas.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.nome}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Tipo"
                select
                SelectProps={{ native: true }}
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                sx={{ minWidth: 200 }}
                {...filtro}
              >
                <option value="">Todos</option>
                {Object.entries(TIPOS_LANCAMENTO).map(([v, r]) => (
                  <option key={v} value={v}>
                    {r}
                  </option>
                ))}
              </TextField>
              <TextField
                label="De"
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                {...filtro}
              />
              <TextField
                label="Até"
                type="date"
                value={fim}
                onChange={(e) => setFim(e.target.value)}
                {...filtro}
              />
            </Box>
            {erro && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {erro}
              </Alert>
            )}
            <DataGrid
              rows={rows}
              autoHeight
              density="compact"
              loading={loading}
              localeText={ptBR_DataGrid}
              rowSelection={false}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              pageSizeOptions={[50, 100]}
              columns={[
                {
                  field: "data",
                  headerName: "Data",
                  minWidth: 105,
                  valueGetter: ({ row }) => dataCalendario(row.data),
                },
                {
                  field: "conta",
                  headerName: "Conta bancária",
                  flex: 1,
                  valueGetter: ({ row }) => row.account?.nome,
                },
                {
                  field: "tipo",
                  headerName: "Tipo",
                  flex: 1,
                  valueGetter: ({ row }) => TIPOS_LANCAMENTO[row.tipo],
                },
                { field: "historico", headerName: "Histórico", flex: 1.4 },
                {
                  field: "natureza",
                  headerName: "Natureza",
                  flex: 1,
                  valueGetter: ({ row }) => row.natureza?.nome || "",
                },
                {
                  field: "valor",
                  headerName: "Valor",
                  flex: 0.8,
                  align: "right",
                  headerAlign: "right",
                  valueGetter: ({ row }) =>
                    row.tipo === "S" ? -row.valor : row.valor,
                  valueFormatter: ({ value }) => brl(value),
                },
                {
                  field: "acoes",
                  headerName: "",
                  sortable: false,
                  minWidth: 100,
                  renderCell: ({ row }) => (
                    <Box display="flex" gap={1}>
                      <Tooltip title="Editar">
                        <MDButton
                          variant="gradient"
                          color="info"
                          iconOnly
                          onClick={() => setDialogo(row)}
                        >
                          <Icon fontSize="small">edit</Icon>
                        </MDButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <MDButton
                          variant="gradient"
                          color="error"
                          iconOnly
                          onClick={() => setRemover(row)}
                        >
                          <Icon fontSize="small">delete_forever</Icon>
                        </MDButton>
                      </Tooltip>
                    </Box>
                  ),
                },
              ]}
            />
          </MDBox>
        </Card>
        <MDDialog
          open={Boolean(remover)}
          handleClose={() => setRemover(null)}
          title="Excluir este lançamento?"
          actions={
            <>
              <Button
                variant="contained"
                sx={{ color: "white !important" }}
                onClick={excluir}
              >
                Excluir
              </Button>
              <Button onClick={() => setRemover(null)}>Cancelar</Button>
            </>
          }
        >
          {remover &&
            `${TIPOS_LANCAMENTO[remover.tipo]} de ${brl(
              remover.valor
            )} em ${dataCalendario(remover.data)}`}
        </MDDialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
